import React, { useContext, useMemo, useState } from 'react';
import { TextInput } from '../../../../../components/TextInput';
import { t } from '../../../../../../types/translation/Translator';
import { TranslationKey } from '../../../../../../types/translation/TranslationKey';
import { ProductMasterDataContext } from '../../../../../../context/ProductMasterDataContext';
import { DropdownSearchSelect } from '../../../../../components/Dropdown';
import ProductMasterDataDropdownItem from '../../../../../components/Dropdown/templates/ProductMasterData/ProductMasterDataDropdownItem';
import { HandlingUnit } from '../../../../../../types/handling_unit/handlingUnit';
import { Feedback } from '../../../../../components/Feedback/feedback';
import { BinContext } from '../../../../../../context/BinContext';
import { Bin } from '../../../../../../types/bin';
import { useToast } from '../../../../../../providers/ToastProvider';
import { Button, ButtonVariant, CancelButton } from '../../../../../components/Button';
import { ActionModalPane } from '../../../../../components/Modal';
import { ProductContext } from '../../../../../../context/ProductContext';
import { HandlingUnitContext } from '../../../../../../context/HandlingUnitContext';
import { UnitOfMeasure } from '../../../../../../types/uom';
import { useRequest } from '../../../../../../requests/common/requestHook';
import { ProductRequest } from '../../../../../../requests/product.request';
import { ProductTransactionSpecifiers } from '../../../../../../types/productTransaction';
import { ToastType } from '../../../../../components/Toast';

interface QuickActionBreakViewProps {
  setOpen: (open: boolean) => void;
  pmdId: string;
  productId?: string;
}

export default function QuickActionBreakView({ setOpen, pmdId, productId }: QuickActionBreakViewProps) {
  const { showToast } = useToast();

  const { products } = useContext(ProductContext);
  const { productMasterData } = useContext(ProductMasterDataContext);
  const { bins } = useContext(BinContext);
  const { handlingUnits } = useContext(HandlingUnitContext);

  const [input, setInput] = useState<HandlingUnit | undefined>();
  const [quantity, setQuantity] = useState<string>('1');

  const product = [...(products.get(pmdId)?.values() || [])].find(p => p.id === productId);
  if (!product) return null;

  const [destinationBin, setDestinationBin] = useState<Bin | undefined>(bins.get(product.binId));

  const resultQuantity = useMemo(() => {
    if (!input || !quantity) return '';

    return parseFloat(quantity) * input.quantity;
  }, [input, quantity]);

  const destinationBins = useMemo(() => {
    return [...bins.values()].filter(item => item.stockLocationId === product.stockLocationId);
  }, [product]);

  const [breakProduct, { loading }] = useRequest(ProductRequest.breakProduct);

  const handleBreak = async () => {
    if (!input) return;

    const item: ProductTransactionSpecifiers = {
      fromProductId: product.id,
      pmdId: input.productMasterDataId,
      quantity: parseFloat(quantity),
      toBinId: destinationBin?.id,
      handlingUnitId: input.id,
    } as ProductTransactionSpecifiers;

    const result = await breakProduct(item);
    if (!result) return;

    showToast({
      type: ToastType.success,
      title: t(TranslationKey.handlingUnitBreakSuccessfullyToastTitle).singular.upper,
    });

    setOpen(false);
  };

  const validate = () => {
    if (
      parseFloat(quantity) >
      parseFloat(UnitOfMeasure.toQuantity(productMasterData.get(pmdId)?.unitOfMeasure, product.unitQuantity))
    ) {
      return false;
    }

    if (parseFloat(quantity) <= 0) return false;
    if (!input) return false;
    if (!destinationBin) return false;

    return true;
  };

  const footer = (
    <div className='flex justify-end gap-2'>
      <CancelButton onClick={() => setOpen(false)} disabled={loading} />
      <Button
        variant={ButtonVariant.primary}
        text={t(TranslationKey.handlingUnitBreak).singular.upper}
        onClick={handleBreak}
        loading={loading}
        disabled={!validate()}
      />
    </div>
  );

  return (
    <ActionModalPane footer={footer}>
      <div className='grid grid-cols-2 gap-2'>
        <TextInput
          label={t(TranslationKey.handlingUnitBreak).singular.label}
          type='number'
          onChange={item => setQuantity(item)}
          value={quantity}
          disabled={!input}
          feedback={item => {
            if (
              parseFloat(item) >
              parseFloat(UnitOfMeasure.toQuantity(productMasterData.get(pmdId)?.unitOfMeasure, product.unitQuantity))
            ) {
              return {
                type: Feedback.Type.error,
                text: t(TranslationKey.insufficientQuantityAvailableInBin).singular.label,
              };
            } else if (parseFloat(item) <= 0) {
              return {
                type: Feedback.Type.error,
                text: t(TranslationKey.handlingUnitQuantityError).singular.label,
              };
            }
          }}
        />
        <DropdownSearchSelect
          label={t(TranslationKey.handlingUnitBreakInto).singular.label}
          placeholder={t(TranslationKey.productReferenceData).singular.label}
          items={Array.from(handlingUnits.values()).filter(hu => hu.parentId === product.productMasterDataId)}
          itemBuilder={item => (
            <ProductMasterDataDropdownItem productMasterData={productMasterData.get(item.productMasterDataId)} />
          )}
          textBuilder={item => productMasterData.get(item.productMasterDataId)?.productName || ''}
          searchBuilder={item => {
            const p = productMasterData.get(item.productMasterDataId);
            return `${p?.productName} ${p?.productNumber}`;
          }}
          value={input}
          onSelect={item => setInput(item)}
        />
        <TextInput label={t(TranslationKey.handlingUnitResult).singular.label} value={resultQuantity} disabled={true} />
        <DropdownSearchSelect
          label={t(TranslationKey.destinationBin).singular.label}
          placeholder={t(TranslationKey.destinationBin).singular.label}
          items={destinationBins}
          textBuilder={item => bins.get(item.id)?.name || ''}
          value={destinationBin}
          onSelect={item => setDestinationBin(item)}
        />
      </div>
    </ActionModalPane>
  );
}
